import React, { useLayoutEffect, useRef, useState } from 'react';
import { useField, useFormikContext } from 'formik';
import {
  Listbox,
  ListboxOptions,
  ListboxOption,
  ListboxButton,
} from '@headlessui/react';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from 'utils/cn';
import RequiredMark from 'components/common/RequiredMark';

type Option = {
  value: string;
  label: string;
};

type SelectClassName =
  | string
  | {
      root?: string;
      label?: string;
      input?: string;
      value?: string;
    };

type CustomSelectProps = {
  name: string;
  label?: string;
  placeholder?: string;
  options: Option[];
  onChange?: (value: string) => void;
  onBlur?: (e: React.FocusEvent) => void;
  error?: string | false;
  disabled?: boolean;
  className?: SelectClassName;
  buttonClassName?: string;
  whiteLabel?: boolean;
  darkBg?: boolean;
  required?: boolean;
  value?: string;
  icon?: React.ReactNode;
  /** Classes for the selected value's text, e.g. a priority colour. Replaces the default white. */
  valueClassName?: string;
};

type SelectFieldProps = Omit<
  CustomSelectProps,
  'name' | 'value' | 'onChange'
> & {
  /** Only used for the label's `htmlFor`; the field isn't Formik-bound here. */
  name?: string;
  value: string;
  onChange: (value: string) => void;
};

export function SelectField({
  name,
  label,
  placeholder = 'Select an option',
  options,
  value,
  onChange,
  onBlur,
  error,
  disabled = false,
  className,
  buttonClassName = '',
  whiteLabel,
  darkBg,
  required = false,
  icon,
  valueClassName,
}: SelectFieldProps) {
  const selectedOption = options.find((option) => option.value === value) || {
    label: placeholder,
    value: '',
  };

  const isError = error;

  const classes =
    typeof className === 'string' ? { root: className } : (className ?? {});
  const rootClassName = classes.root ?? '';
  const inputClassName = cn(classes.input, buttonClassName);

  const hasHeight = (c: string) => c.split(' ').some((x) => x.startsWith('h-'));

  const heightClass = hasHeight(rootClassName)
    ? 'h-full'
    : hasHeight(inputClassName)
      ? 'h-auto'
      : 'h-[46px]';

  // headlessui's own --button-width CSS variable does not end up matching
  // the trigger's real width here, so this measures it directly instead.
  const triggerWrapperRef = useRef<HTMLDivElement>(null);
  const [triggerWidth, setTriggerWidth] = useState<number>();

  const measureTrigger = () => {
    const el = triggerWrapperRef.current;
    if (el) setTriggerWidth(el.getBoundingClientRect().width);
  };

  useLayoutEffect(() => {
    const el = triggerWrapperRef.current;
    if (!el) return;
    measureTrigger();
    const observer = new ResizeObserver(measureTrigger);
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className={cn(`w-full`, rootClassName)} data-field={name} id={name}>
      {label && (
        <label
          htmlFor={name}
          className={cn(
            `block ${
              whiteLabel ? 'text-neutral font-medium' : 'text-[#9CA3AF]'
            } text-[14px] font-normal leading-[100%] mb-[11px] `,
            classes.label,
          )}
        >
          {label}
          {required && <RequiredMark />}
        </label>
      )}

      <Listbox value={value} onChange={onChange} disabled={disabled}>
        {/* Re-measure on every open, not just on mount — a parent that's
            still mid enter-transition (e.g. this field inside a scaling
            modal) can report a transform-shrunk width at mount time, and
            transform changes don't fire ResizeObserver since the layout box
            itself never actually changes size. By the time the user can
            click to open this, any such transition has long settled. */}
        <div
          ref={triggerWrapperRef}
          onPointerDown={measureTrigger}
          className={`relative ${heightClass} rounded-[inherit]`}
        >
          <ListboxButton
            name={name}
            data-field={name}
            onBlur={onBlur}
            className={cn(
              `relative w-full h-full px-4 border ${
                darkBg ? 'bg-primary-light' : 'bg-[#1E2230]'
              } focus:outline-none focus:ring-1 ${
                isError
                  ? 'border-red-500 focus:ring-red-500'
                  : 'border-[#2A2F3D] focus:ring-[#2A2F3D]'
              } ${
                disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'
              } ${
                inputClassName.includes('rounded')
                  ? ''
                  : rootClassName.includes('rounded')
                    ? 'rounded-[inherit]'
                    : 'rounded-[8px]'
              }`,
              inputClassName,
            )}
          >
            {icon && (
              <span className="absolute inset-y-0 left-0 flex items-center pl-2.5">
                {icon}
              </span>
            )}
            <span
              className={cn(
                'block truncate text-left text-[16px]',
                icon && 'pl-5',
                value
                  ? valueClassName || 'text-white'
                  : 'font-normal text-base text-[#374151]',
                classes.value,
              )}
            >
              {selectedOption.label}
            </span>
            <span className="absolute inset-y-0 right-0 flex items-center pr-4">
              <ChevronDown
                className={`h-4 w-4 ${
                  disabled ? 'text-gray-400' : 'text-[#9CA3AF]'
                }`}
                aria-hidden="true"
              />
            </span>
          </ListboxButton>

          <ListboxOptions
            anchor="bottom start"
            transition
            style={triggerWidth ? { width: `${triggerWidth}px` } : undefined}
            className={`[--anchor-gap:4px] [--anchor-max-height:15rem] z-[9999] overflow-auto rounded-md  ${
              darkBg ? 'bg-primary-light' : 'bg-primary-intemediate'
            } py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm transition duration-100 ease-in data-[closed]:opacity-0`}
          >
            {options.map((option) => (
              <ListboxOption
                key={option.value}
                className={({ active }) =>
                  `relative cursor-default select-none py-2 pl-10 pr-4 text-neutral ${
                    active ? 'bg-primary-subtle' : ''
                  }`
                }
                value={option.value}
              >
                {({ selected }) => (
                  <>
                    <span
                      className={`block truncate ${
                        selected ? 'font-medium' : 'font-normal'
                      }`}
                    >
                      {option.label}
                    </span>
                    {selected ? (
                      <span className="absolute inset-y-0 left-0 flex items-center pl-3">
                        <Check className="h-5 w-5" aria-hidden="true" />
                      </span>
                    ) : null}
                  </>
                )}
              </ListboxOption>
            ))}
          </ListboxOptions>
        </div>
      </Listbox>

      {isError && <p className="mt-1 text-sm text-red-500">{isError}</p>}
    </div>
  );
}

export function CustomSelect({
  name,
  onChange,
  onBlur,
  ...rest
}: CustomSelectProps) {
  const [field, meta] = useField(name);
  const { setFieldValue } = useFormikContext();

  const handleChange = (value: string) => {
    setFieldValue(name, value);
    onChange?.(value);
  };

  const handleBlur = (e: React.FocusEvent) => {
    field.onBlur(e);
    onBlur?.(e);
  };

  return (
    <SelectField
      {...rest}
      name={name}
      value={field.value ?? ''}
      onChange={handleChange}
      onBlur={handleBlur}
      error={meta.touched && meta.error ? meta.error : undefined}
    />
  );
}
